import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { store, useDB } from '../data/store';
import { feed, isFollowing, me, trendingTags, yearCounts, type FeedFilter, type Tab } from '../data/queries';
import { Composer, PostCard } from '../components/Post';
import { Rainbow, Stamp, TagLink, YearLink } from '../components/Bits';
import { useUI } from '../components/ui';
import { yearOfTheDay } from '../components/Layout';
import { RANKING_EXPLAINED } from '../lib/ranking';
import { isValidTag, normalizeTag } from '../lib/text';
import { BRANCHES, BRANCH_COLOR, type Branch } from '../lib/validation';
import { eraOf, isYear, LAST_YEAR, yearColor, yearFontStyle, yearInfo } from '../lib/years';
import { NotFound } from './Static';

const TABS: [Tab, string][] = [['foryou', 'For you'], ['following', 'Following'], ['latest', 'Latest']];

/** Tabs, branch filter and the list of memories. Feeds end: no endless scrolling. */
export function FeedView({ filter, empty, endText, showBranches = true }: { filter: FeedFilter; empty: ReactNode; endText: string; showBranches?: boolean }) {
  const db = useDB();
  const [tab, setTab] = useState<Tab>('foryou');
  const [branch, setBranch] = useState<Branch | undefined>();
  const [limit, setLimit] = useState(20);
  const [why, setWhy] = useState(false);
  const list = useMemo(() => feed(db, tab, { ...filter, branch }, Date.now()), [db, tab, filter, branch]);
  const shown = list.slice(0, limit);
  return (
    <>
      {showBranches && <div className="branches" role="group" aria-label="Topics">
        {BRANCHES.map(b => (
          <button key={b} className="br" aria-pressed={branch === b} style={{ ['--bc' as string]: `var(--rb-${BRANCH_COLOR[b]})` }}
            onClick={() => setBranch(branch === b ? undefined : b)}>{b}</button>))}
      </div>}
      <div className="tabs" role="tablist" aria-label="Order">
        {TABS.map(([k, label]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{label}</button>)}
        <button className="why" onClick={() => setWhy(!why)} aria-expanded={why}>Why these posts?</button>
      </div>
      {why && <p className="small muted">{RANKING_EXPLAINED}</p>}
      <div className="feed">
        {shown.length === 0
          ? <div className="empty">{tab === 'following' && !db.meId ? 'Pick a name and follow people, years or hashtags to fill this tab.' : tab === 'following' ? 'Follow people, years, hashtags or communities to see them here.' : empty}</div>
          : shown.map(p => <PostCard key={p.id} post={p} />)}
      </div>
      {list.length > limit
        ? <div className="row center"><button className="btn ghost" onClick={() => setLimit(limit + 20)}>Show {Math.min(20, list.length - limit)} more</button></div>
        : shown.length > 0 && <p className="end">{endText}</p>}
    </>
  );
}

export function Home() {
  const db = useDB();
  const day = yearOfTheDay();
  const filter = useMemo(() => ({}), []);
  return (
    <div className="grid">
      <section className="col">
        <div className="hero">
          <span className="line">Nostalgia will haunt us.</span>
          <h1>Every year you lived <Rainbow text="still remembers you" />.</h1>
          <p>Memories filed by the year they're about, from 1995 to right now. Pick a year, follow a #hashtag, start an idea, or let the archive pull you back.</p>
        </div>
        <Composer compact />
        <FeedView filter={filter} empty="No memories yet. Share the first one above." endText="You're all caught up. Pick a year to keep going." />
      </section>
      <aside className="col side">
        <Haunting day={day} />
        <TrendingTags />
        <Leaderboard />
        {!me(db) && <PickName />}
      </aside>
    </div>
  );
}

function Haunting({ day }: { day: number }) {
  const prompts = ['What song takes you straight back to {y}?', 'What did your bedroom look like in {y}?', 'What was everyone at school obsessed with in {y}?', 'What\'s the most {y} thing you can think of?', 'What did a perfect Saturday look like in {y}?', 'What\'s something from {y} nobody talks about anymore?'];
  const prompt = prompts[(Math.floor(Date.now() / 86_400_000) * 5) % prompts.length]!.replace('{y}', String(day));
  return (
    <div className="panel">
      <h2>Tonight's haunting</h2>
      <p>New every day, the same for everyone.</p>
      <div className="idea" style={{ ['--yc' as string]: yearColor(day) }}><span className="k">Year of the day</span><p><YearLink year={day} /> {yearInfo(day)[0]}.</p></div>
      <div className="idea"><span className="k">Memory prompt</span><p>{prompt}</p><Link className="small" to={`/new?year=${day}`}>Answer it</Link></div>
    </div>
  );
}

function TrendingTags() {
  const db = useDB();
  const tags = useMemo(() => trendingTags(db, Date.now(), 12), [db]);
  return (
    <div className="panel">
      <h2>Trending hashtags</h2>
      <p>Anyone can start one: just write #something in a memory.</p>
      <div className="tags">{tags.map(t => <TagLink key={t.tag} tag={t.tag} />)}</div>
      <Link className="small" to="/tags">All hashtags</Link>
    </div>
  );
}

function Leaderboard() {
  const db = useDB();
  const rows = useMemo(() => [...yearCounts(db).entries()].sort((a, b) => b[1] - a[1]).slice(0, 6), [db]);
  const max = rows[0]?.[1] ?? 1;
  return (
    <div className="panel">
      <h2>Year leaderboard</h2>
      <p>Ranked by "I remember this" taps, not views. Example numbers included.</p>
      <ol className="lb">{rows.map(([y, v], i) => (
        <li key={y} style={{ ['--yc' as string]: yearColor(y) }}><span className="rk">{i + 1}</span><YearLink year={y} /><span className="b2"><i style={{ width: `${(v / max) * 100}%` }} /></span><span className="v">{v}</span></li>))}
      </ol>
    </div>
  );
}

function PickName() {
  const ui = useUI();
  return <div className="panel"><h2>Join in</h2><p>Pick a name to post, comment, follow and start hashtags, ideas and communities.</p><button className="btn hot" onClick={ui.askProfile}>Pick a name</button></div>;
}

export function YearPage() {
  const { year: raw } = useParams();
  const db = useDB(), ui = useUI();
  const year = Number(raw);
  const filter = useMemo(() => ({ year }), [year]);
  if (!isYear(year)) return <NotFound />;
  const era = eraOf(year), [title, desc] = yearInfo(year);
  const following = isFollowing(db, 'year', String(year));
  return (
    <div className="grid" style={{ ['--yc' as string]: yearColor(year) }}>
      <section className="col">
        <div className="hero">
          <span className="line"><span style={{ color: yearColor(year) }}>{era.name} era</span><span>{era.from} to {Math.min(era.to, LAST_YEAR)}</span></span>
          <div className="bigyear" aria-hidden="true" style={{ color: yearColor(year), ...(db.settings.yearFonts ? yearFontStyle(year) : {}) }}>{year}</div>
          <h1 className="ytitle"><span className="sr">{year}: </span>{title}</h1>
          <p>{desc}</p>
          {year === 2016 && <div className="trend"><b>2026 is the new 2016</b><span>Ten years later, everyone is posting their 2016 photos again.</span></div>}
          <div className="row">
            <button className="btn ghost" aria-pressed={following} onClick={() => ui.act(() => store.toggleFollow('year', String(year)), on => ui.toast(on ? `Following ${year}` : `Unfollowed ${year}`))}>{following ? `Following ${year}` : `Follow ${year}`}</button>
            {year - 10 >= 1995 && <Link className="btn ghost" to={`/y/${year - 10}`}>{year - 10}</Link>}
            {year + 10 <= LAST_YEAR && <Link className="btn ghost" to={`/y/${year + 10}`}>{year + 10}</Link>}
          </div>
        </div>
        <Composer compact defaults={{ year }} key={year} />
        <FeedView filter={filter} empty={`No memories from ${year} yet. Be the first to file one.`} endText={`That's everything from ${year} so far. You're all caught up.`} />
      </section>
      <aside className="col side">
        <div className="panel"><h2>The most <span style={{ color: yearColor(year), ...yearFontStyle(year) }}>{year}</span> thing?</h2><p>Start an idea and let everyone answer.</p><Link className="btn ghost sm" to={`/ideas?year=${year}`}>Start an idea about {year}</Link></div>
        <div className="panel"><h2>Year of the stamp</h2><Stamp className="panel-stamp" text={`'${String(year).slice(2)} 1 1`} label={`January 1, ${year}`} style={{ color: yearColor(year) }} /><p>{era.look}</p></div>
        <TrendingTags />
      </aside>
    </div>
  );
}

export function TagPage() {
  const { tag: raw = '' } = useParams();
  const db = useDB(), ui = useUI();
  const tag = normalizeTag(raw);
  const filter = useMemo(() => ({ tag }), [tag]);
  if (!isValidTag(tag)) return <NotFound />;
  const following = isFollowing(db, 'tag', tag);
  return (
    <div className="grid">
      <section className="col">
        <div className="hero">
          <span className="line">Hashtag</span>
          <h1><TagLink tag={tag} /></h1>
          <p>Every memory with #{tag}. Anyone can add to it by writing #{tag} in a memory.</p>
          <div className="row">
            <button className="btn ghost" aria-pressed={following} onClick={() => ui.act(() => store.toggleFollow('tag', tag), on => ui.toast(on ? `Following #${tag}` : `Unfollowed #${tag}`))}>{following ? `Following #${tag}` : `Follow #${tag}`}</button>
          </div>
        </div>
        <Composer compact defaults={{ title: '' }} key={tag} />
        <FeedView filter={filter} empty={`Nothing with #${tag} yet. Write #${tag} in a memory to start it.`} endText={`That's every memory with #${tag}.`} />
      </section>
      <aside className="col side"><TrendingTags /></aside>
    </div>
  );
}

type SectionKey = 'kindness' | 'forgotten' | 'there' | 'capsule';
const SECTIONS: Record<SectionKey, { line: string; h1: [string, string, string]; p: string; empty: string }> = {
  kindness: { line: 'Kindness', h1: ['Good things people ', 'never forgot', '.'], p: 'Kind things people did, filed by the year they happened. Read one, then tap Pass it on and share a kind thing of your own.', empty: 'No kindness stories yet. Share one: pick the Kindness topic when you post.' },
  forgotten: { line: 'Forgotten Forever?', h1: ['Things the internet ', 'buried', '.'], p: 'Lost games, dead websites, toys nobody can name. Describe it, tick "I don\'t know what this is", and someone will know.', empty: 'No hunts open. Tick "I don\'t know what this is" when you post to start one.' },
  there: { line: 'You had to be there', h1: ['Moments you ', 'can\'t explain', ' to anyone who missed them.'], p: 'The things that only make sense if you were around at the time.', empty: 'Nothing here yet. Tick "You had to be there" when you post.' },
  capsule: { line: 'Time Capsule', h1: ['Memories you ', 'kept', '.'], p: 'Tap Save on any memory to keep it here. Only you can see your Time Capsule.', empty: 'Nothing saved yet. Tap Save on a memory to keep it here.' },
};
export function Section({ k }: { k: SectionKey }) {
  const db = useDB();
  const s = SECTIONS[k];
  const filter = useMemo<FeedFilter>(() => k === 'kindness' ? { kindness: true } : k === 'capsule' ? { savedBy: db.meId ?? '__nobody__' } : { format: k }, [k, db.meId]);
  return (
    <div className="grid">
      <section className="col">
        <div className="hero"><span className="line">{s.line}</span><h1>{s.h1[0]}<Rainbow text={s.h1[1]} offset={k.length} />{s.h1[2]}</h1><p>{s.p}</p></div>
        {k !== 'capsule' && <Composer compact defaults={k === 'kindness' ? { branch: 'Kindness' } : { format: k === 'forgotten' ? 'forgotten' : 'there' }} />}
        <FeedView filter={filter} empty={s.empty} endText="You're all caught up." showBranches={k !== 'kindness'} />
      </section>
      <aside className="col side"><TrendingTags /></aside>
    </div>
  );
}
